import React from 'react';
import { observer } from 'mobx-react-lite';
import { confirmAlert } from 'react-confirm-alert';
import Linkify from 'react-linkify';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { useTranslation } from 'react-i18next';
import * as utils from '../../../../helpers/utils';
import styles from './Message.module.css';
import { useRootStore } from '../../../../mobx/bridge';
import CustomConfirmAlert from '../CustomConfirmAlert/CustomConfirmAlert';
import CustomImage from '../CustomImage/CustomImage';
import ImageErrorBoundary from '../CustomImage/ImageErrorBoundary';
import missingFile from '../../../../assets/images/missing-file-min.png';
import { formatMessageTimestamp } from '../../../../helpers/dateHelpers';
import { getContactFullName, getCustomerFromSmsChannel } from '../../../../helpers/contactHelpers';

const LoaderColor = {
  colorPrimary: `${styles.loadBarPrimary}`,
};

const LinkProps = { target: '_blank', rel: 'nofollow noopener noreferrer', draggable: 'false' };

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const Message = observer(function Message(props) {
  const { commonStore, settingsStore, sendingEvents } = useRootStore();
  const { t } = useTranslation();
  const { fromMe, messageData, isFirstMessage, isFromInternal, showInitials, isHidden } = props;
  const [anchorEl, setAnchorEl] = React.useState(null);

  const isS3ImageFetched = React.useRef(null);
  const s3FetchTimeout = React.useRef(null);

  const settings = settingsStore.getLoggedUserSettings;

  const isImageUploadAndHasExpireParam =
    messageData.FileName &&
    messageData.FilePath &&
    utils.getUrlParameter('Expires', messageData.FilePath);
  const [showLoader, setShowLoader] = React.useState(
    isImageUploadAndHasExpireParam && new Date(isImageUploadAndHasExpireParam * 1000) < new Date()
  );

  const messageBodyStyle = [styles.messageBody];
  const initialsStyle = [styles.initials];
  const systemMessageStyle = [styles.systemMessage];

  if (settings?.SelectedChannel?.ChannelType) {
    if (settings.SelectedChannel.ChannelType.TypeId === utils.ChannelTypeEnum.WebChat.TypeId) {
      systemMessageStyle.push(styles.webChatSystemMessage);
      if (fromMe || isFromInternal) {
        messageBodyStyle.push(styles.webChatColor);
        initialsStyle.push(styles.webChatColor);
      }
    } else if (fromMe || isFromInternal) {
      initialsStyle.push(styles.textChatColor);
    }
  }

  React.useEffect(() => {
    if (!isS3ImageFetched.current && showLoader) {
      sendingEvents.socketEmitGetImage(messageData._id);

      isS3ImageFetched.current = true;
      s3FetchTimeout.current = setTimeout(() => {
        if (showLoader) {
          setShowLoader(false);
        }
      }, 5000);
    }

    return () => {
      if (s3FetchTimeout.current) {
        clearTimeout(s3FetchTimeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preventDrag = (event) => {
    event.preventDefault();
  };

  const retainScrollPosition = () => {
    commonStore.setSpecificScrollHeight(commonStore.messagesContainerRef.current.scrollTop);
  };

  const handleRetrySendMessage = () => {
    if (
      messageData.MessageType.TypeId === utils.MessageTypeEnum.SMS.TypeId &&
      !commonStore.locationTrackingNumber
    ) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <CustomConfirmAlert
            title={t('Common.Warning')}
            message={t('Common.NoActiveTrackingNumberForLocationWarning')}
            falseText={t('Common.Close')}
            falseEvent={onClose}
          />
        ),
      });

      return;
    }

    const otherContact = settings.SelectedChannel.Contacts.find(
      (c) => c.ContactType.TypeId === utils.ContactTypeEnum.Customer.TypeId && !c.IsDeleted
    );

    if (
      messageData.MessageType.TypeId === utils.MessageTypeEnum.SMS.TypeId &&
      messageData.Channel &&
      !messageData.Channel.CustomerPhoneNumber
    ) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <CustomConfirmAlert
            title={t('Common.Warning')}
            message={t('Common.ContactHasNoPhoneNumbers', {
              contactName: otherContact.getFullName,
            })}
            falseText={t('Common.Close')}
            falseEvent={onClose}
          />
        ),
      });

      return;
    }

    sendingEvents.socketEmitRetrySendMessage(messageData);
  };

  const handleDeleteTextMessage = () => {
    commonStore.setOldScrollHeight(
      commonStore.messagesContainerRef.current.scrollHeight -
        commonStore.messagesContainerRef.current.scrollTop
    );
    sendingEvents.socketEmitDeleteMessage(messageData);
  };

  const confirmDeleteTextMessage = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <CustomConfirmAlert
          title={t('Common.Warning')}
          message={t('Message.ConfirmDeleteMessage')}
          trueText={t('Common.Confirm')}
          trueEvent={handleDeleteTextMessage}
          falseText={t('Common.Close')}
          falseEvent={onClose}
        />
      ),
    });
  };

  const showOptions = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseOptions = () => {
    setAnchorEl(null);
  };

  const toggleMessageVisibility = () => {
    retainScrollPosition();
    sendingEvents.socketEmitToggleSingleMessageVisibility(messageData._id);
    setAnchorEl(null);
  };

  let messageTextElementToRender = null;

  if (messageData.FilePath) {
    messageTextElementToRender = !showLoader ? (
      <ImageErrorBoundary
        fallback={
          <div className={styles.failedToloadContainer}>
            <img
              src={missingFile}
              alt={t('Common.MissingFile')}
              height="192"
              onDragStart={preventDrag}
            />

            <span className={styles.fileNotFoundText}>{t('Message.FileNotFound')}</span>
          </div>
        }
      >
        <React.Suspense
          fallback={<CircularProgress size={251} thickness={2} classes={LoaderColor} />}
        >
          <a
            href={messageData.FilePath}
            target="_blank"
            rel="noopener noreferrer"
            onDragStart={preventDrag}
          >
            <CustomImage url={messageData.FilePath} alt={t('Message.MessageFile')} />
          </a>
        </React.Suspense>
      </ImageErrorBoundary>
    ) : (
      <CircularProgress size={251} thickness={2} classes={LoaderColor} />
    );
  }

  let senderNameElement = '';

  if (isFirstMessage && messageData.Sender && messageData.Sender.ContactType) {
    const matchedChannelContact = getCustomerFromSmsChannel(settings?.SelectedChannel);

    if (
      matchedChannelContact &&
      messageData.Sender.ContactType.TypeId === utils.ContactTypeEnum.Customer.TypeId
    ) {
      senderNameElement = (
        <span className={styles.contactName}>
          {getContactFullName(
            matchedChannelContact,
            settings?.SelectedChannel?.CustomerPhoneNumber
          )}
        </span>
      );
    } else if (
      messageData.Sender.FirstName ||
      messageData.Sender.LastName ||
      messageData.Sender.CpUsername ||
      messageData.Sender.Emails?.length
    ) {
      senderNameElement = (
        <span className={styles.contactName}>{messageData.Sender.getFullName}</span>
      );
    }
  }

  let senderInitials;
  if (showInitials) {
    const initials = messageData.Sender?.getInitials || '??';

    senderInitials = <span className={initialsStyle.join(' ')}>{initials}</span>;
  }

  let retrySendElement;
  if (messageData.SMSDeliveryStatus) {
    if (
      messageData.SMSDeliveryStatus.TypeId === utils.SMSDeliveryStatusEnum.Sending.TypeId ||
      messageData.SMSDeliveryStatus.TypeId === utils.SMSDeliveryStatusEnum.Pending.TypeId
    ) {
      retrySendElement = (
        <div>
          <span
            className={styles.smsDeliveryStatusMsg}
            data-testid={messageData.SMSDeliveryStatus.TypeName}
          >
            {messageData.SMSDeliveryStatus.TypeName}
            ...
          </span>
        </div>
      );
    } else if (messageData.SMSDeliveryStatus.TypeId === utils.SMSDeliveryStatusEnum.Retry.TypeId) {
      retrySendElement = (
        <React.Fragment>
          <span className={styles.failedToSendSmsText} data-testid="failedToSendMessage">
            {t('Message.FailedToSendTextMessage')}
          </span>

          <div className={styles.smsButtonsContainer}>
            <button type="button" className={styles.retryButton} onClick={confirmDeleteTextMessage}>
              {t('Message.DeleteFailedMessage')}
            </button>

            <button
              type="button"
              className={styles.retryButton}
              onClick={handleRetrySendMessage}
              data-testid="retrySendBtn"
            >
              {t('Message.ClickHereToRetry')}
            </button>
          </div>
        </React.Fragment>
      );
    } else if (
      messageData.SMSDeliveryStatus.TypeId === utils.SMSDeliveryStatusEnum.Undeliverable.TypeId
    ) {
      retrySendElement = (
        <React.Fragment>
          <span className={styles.failedToSendSmsText}>
            {t('Message.MessageCannotBeDelivered')}
          </span>

          <div className={styles.smsButtonsContainer}>
            <button
              type="button"
              className={styles.undeliverableDeleteButton}
              onClick={confirmDeleteTextMessage}
            >
              {t('Message.DeleteFailedMessage')}
            </button>
          </div>
        </React.Fragment>
      );
    }
  }

  const optionsButton = (
    <div>
      <button type="button" onClick={showOptions} className={styles.moreOptionsBtn} />

      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseOptions}
      >
        <MenuItem onClick={toggleMessageVisibility}>
          {isHidden ? t('Message.ShowMessage') : t('Message.HideMessage')}
        </MenuItem>
      </Menu>
    </div>
  );

  const messageToolTip = (
    <div className={styles.tooltipContainer}>
      <span>{formatMessageTimestamp(messageData.Timestamp)}</span>

      <span>
        {t('Message.MessageSentVia', {
          messageType: utils.prettifyMessageType(messageData?.MessageType?.TypeName),
        })}
      </span>
    </div>
  );

  if (
    messageData.MessageType.TypeId === utils.MessageTypeEnum.System.TypeId ||
    messageData.MessageType.TypeId === utils.MessageTypeEnum.AutoReply.TypeId ||
    messageData.MessageType.TypeId === utils.MessageTypeEnum.WelcomeChannelMessage.TypeId ||
    messageData.MessageType.TypeId === utils.MessageTypeEnum.ClosedChannelMessage.TypeId
  ) {
    return (
      <Linkify properties={LinkProps}>
        <div className={[styles.systemMessageContainer, isHidden ? styles.isHidden : ''].join(' ')}>
          <Tooltip
            PopperProps={{ disablePortal: true }}
            arrow
            enterDelay={1000}
            title={messageToolTip}
          >
            <div className={systemMessageStyle.join(' ')}>
              {messageTextElementToRender || messageData.MessageText}

              {optionsButton}
            </div>
          </Tooltip>
        </div>
      </Linkify>
    );
  }

  return (
    <React.Fragment>
      <div
        className={[
          styles.message,
          fromMe || isFromInternal ? styles.fromMe : '',
          isHidden ? styles.isHidden : '',
        ].join(' ')}
        data-testid={`${messageData?.MessageText?.replace(/\n/g, '')}`}
      >
        {senderInitials}

        <Tooltip
          PopperProps={{ disablePortal: true }}
          arrow
          enterDelay={1000}
          title={messageToolTip}
          placement={fromMe || isFromInternal ? 'left' : 'right'}
        >
          <div className={styles.messageBodyHolder}>
            {senderNameElement}

            <Linkify properties={LinkProps}>
              <div
                className={[
                  messageBodyStyle.join(' '),
                  !messageData.MessageText && messageData.FilePath ? styles.isImageOnly : '',
                  messageData.MessageText && messageData.FilePath ? styles.isImageAndText : '',
                ].join(' ')}
              >
                {messageData.MessageText ? <span>{messageData.MessageText}</span> : null}

                {messageTextElementToRender ? (
                  <div className={styles.imageContainer}>{messageTextElementToRender}</div>
                ) : null}
              </div>

              {optionsButton}
            </Linkify>
          </div>
        </Tooltip>
      </div>

      {retrySendElement}
    </React.Fragment>
  );
});
Message.whyDidYouRender = true;
Message.displayName = 'Message';

export default Message;
